import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { Mail, Lock, Loader2, Sparkles } from "lucide-react";
import {
  handleGoogleSignIn,
} from "../lib/supabaseClient";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // ProtectedRoute passes a location object; some callers pass a bare path
  // string. Reading only `.pathname` silently dropped the latter and dumped
  // shoppers on /submit halfway through checkout.
  const requestedFrom = location.state?.from;
  const from =
    (typeof requestedFrom === "string" ? requestedFrom : requestedFrom?.pathname) || "/submit";

  useEffect(() => {
    const oauthError = searchParams.get("error");
    if (oauthError === "auth_failed") {
      toast.error("Google sign-in failed. Try again or use email and password.");
      navigate("/login", { replace: true });
    }
  }, [navigate, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      login(response.data, response.data.token);
      toast.success("Welcome back!");
      navigate(from, { replace: true });
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    const returnPath = from && from.startsWith("/") ? from : "/submit";
    setGoogleLoading(true);
    try {
      await handleGoogleSignIn(returnPath);
    } catch (error) {
      console.error("Google login error:", error);
      toast.error(error.message || "Google sign-in failed. Try email and password.");
      setGoogleLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100000] min-h-screen flex bg-white">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8">
            <Sparkles className="h-8 w-8 text-orange-600" />
            <div>
              <span className="font-bold text-xl tracking-tight">CheepSweepz</span>
              <span className="text-orange-600 text-xs ml-1">CONCIERGE</span>
            </div>
          </Link>

          <h1 className="text-2xl font-bold text-slate-900 mb-2" data-testid="login-title">
            Welcome back
          </h1>
          <p className="text-slate-600 mb-8">
            Sign in to submit product links and manage your imports
          </p>

          {/* Google Login */}
          <Button
            variant="outline"
            className="w-full mb-6"
            onClick={handleGoogleLogin}
            disabled={googleLoading}
            data-testid="google-login-button"
          >
            {googleLoading ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Redirecting to Google...
              </>
            ) : (
              <>
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
              </>
            )}
          </Button>

          <div className="relative mb-6">
            <Separator />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4 text-sm text-slate-500">
              or
            </span>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10"
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full py-3 px-4 bg-orange-600 hover:bg-orange-700 text-white font-bold text-base rounded-lg shadow-md h-auto transition-colors focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 disabled:opacity-100 disabled:bg-slate-200 disabled:text-slate-600 disabled:cursor-not-allowed"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-600 mt-6">
            Don&apos;t have an account?{" "}
            <Link
              to="/register"
              className="text-orange-700 hover:underline font-medium"
              data-testid="register-link"
            >
              Create account
            </Link>
          </p>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:flex flex-1 bg-slate-900 items-center justify-center p-12 noise-texture">
        <div className="max-w-md text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Shop the World,
            <br />
            <span className="text-orange-500">Delivered to You</span>
          </h2>
          <p className="text-slate-400">
            Access products from Amazon, AliExpress, eBay, TEMU & SHEIN with
            transparent all-inclusive pricing and hassle-free delivery.
          </p>
        </div>
      </div>
    </div>
  );
}
